import React, { useCallback, useMemo, useState } from 'react';
import SubPage, { PageActions } from '#/common/components/SubPage';
import WorkOrders from '../components/WorkOrders';
import { Tab } from '#/common/components/SubPage/SubPageProps';
import useDocumentTitle from '#/common/hooks/useDocumentTitle';
import If from '#/common/components/If';
import { Navigate, useSearchParams } from 'react-router-dom';
import WOTemplate from '../../wo-template/components/WOTemplates';
import useWorkOrderPropsCounts from '../hooks/useWorkOrderPropsCountsQuery';
import useSession from '#/common/hooks/useSession';
import { UserRole } from '#/common/types/userTypes';
import { PageAction } from '#/app/providers/PageProvider';
import type { Filter } from '#/common/components/DataGrid';
import Switch from 'devextreme-react/switch';
import styled from 'styled-components';
import {
  fsmParentVisibleColumns,
  fsmLineItemsVisibleColumns,
} from '../components/WorkOrderDataGrid/columns';
import { color } from '@nuvolo/nuux-theme/utils';

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .dx-switch {
    width: 44px;
    height: 24px;
  }

  .dx-switch-on-value {
    .dx-switch-container::before {
      background-color: ${color('primary', 'dark')}};
    }

    .dx-switch-handle::before {
      background-color: ${color('ui', 'dark2')}};
    }

    .dx-switch-inner {
      padding-right: 4px;
    }
    .dx-switch-handle {
      margin-left: 0 !important;
    }
  }

  .dx-switch-container {
    height: 24px;
    &::before {
      height: 24px;
      background-color: ${color('secondary', 'light2')}};
    }
  }
  .dx-switch-inner {
    align-items: center;
  }
  .dx-switch-handle {
    flex-basis: 16px;
    width: 16px;
    height: 16px;
    margin-left: 4px;
    margin-top: 3px;
  }
`;

const SwitchLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

export type WorkOrdersTab = 'list' | 'templates' | 'fsm-list';

export const defaultTab: WorkOrdersTab = 'list';

export interface WorkOrdersProps {
  readonly tab: WorkOrdersTab;
}

const WorkOrdersPage: React.FC<WorkOrdersProps> = ({ tab }) => {
  const { user } = useSession();
  const isRoleTechnician = !!user && user.role === UserRole.technician && user.id;
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId') || undefined;
  const [lineItemsView, setLineItemsView] = useState(false);

  const tabs = [
    {
      key: 'fsm-list',
      text: 'FSM Work Orders',
      extra: {
        newPath: '/work-orders/fsm/new',
        newLabel: 'FSM Work Order',
      },
    },
    {
      key: 'list',
      text: isRoleTechnician ? 'My Work Orders' : 'TMM Work Orders',
      extra: {
        newPath: '/work-orders/new',
        newLabel: 'Work Order',
      },
    },
    {
      key: 'templates',
      text: 'Templates',
      extra: {
        newPath: '/work-orders/templates/new',
        newLabel: 'Template',
      },
    },
  ] satisfies Tab<WorkOrdersTab>[];

  const selectedTab = tabs.find((t) => t.key === tab);
  const title =
    selectedTab?.key === 'list' || selectedTab?.key === 'fsm-list'
      ? selectedTab?.text
      : `Work Order ${selectedTab?.text}`;
  useDocumentTitle(title);

  const {
    workOrders = 0,
    fsmParentWorkOrders = 0,
    fsmLineItemsWorkOrders = 0,
    templates = 0,
  } = useWorkOrderPropsCounts();

  const getPath = useCallback(
    (tab: string) => {
      if (tab === 'list' && isRoleTechnician) {
        const filters = ['assignedToId', '=', user?.id];
        const params = new URLSearchParams();
        params.set('filters', JSON.stringify(filters));
        return `list?${params.toString()}`;
      }

      return tab;
    },
    [user, isRoleTechnician],
  );

  const workOrdersTabs = useMemo(
    () =>
      tabs.map((t) => {
        if (t.key === 'list') {
          return {
            ...t,
            count: workOrders,
            path: getPath(t.key),
          };
        }
        if (t.key === 'fsm-list') {
          return {
            ...t,
            count: lineItemsView ? fsmLineItemsWorkOrders : fsmParentWorkOrders,
            path: 'fsm-list',
          };
        }
        if (t.key === 'templates') {
          return {
            ...t,
            count: templates,
            path: getPath(t.key),
          };
        }
        return t;
      }),
    [
      isRoleTechnician,
      workOrders,
      fsmParentWorkOrders,
      fsmLineItemsWorkOrders,
      lineItemsView,
      templates,
      getPath,
    ],
  ) as Tab[];

  const invalidTab = tabs.every((t) => t.key !== tab);
  if (invalidTab) {
    const redirectTo = `../${defaultTab}`;
    return <Navigate to={redirectTo} replace />;
  }

  const newPath =
    selectedTab?.key && `${selectedTab.extra.newPath}${assetId ? `?assetId=${assetId}` : ''}`;

  const isFsmTab = tab === 'fsm-list';

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: 'new-wo-item',
        alwaysShow: true,
        type: 'button',
        props: {
          type: 'submit',
          icon: 'add',
          label: `Create New ${selectedTab?.extra.newLabel}`,
          responsive: true,
          path: newPath,
          disabled: isFsmTab,
        },
      },
    ],
    [newPath, selectedTab, isFsmTab],
  );

  return (
    <SubPage title={title} tabs={workOrdersTabs} selectedTab={tab} type="list">
      <PageActions actions={actions} />
      <If condition={tab === 'list'}>
        <WorkOrders hideNewAction showOnlyInternal />
      </If>
      <If condition={tab === 'fsm-list'}>
        <WorkOrders
          key={lineItemsView ? 'line-items' : 'parent'}
          hideNewAction
          hideBulkActions
          visibleColumns={lineItemsView ? fsmLineItemsVisibleColumns : fsmParentVisibleColumns}
          gridId={lineItemsView ? 'fsm-line-items-work-orders' : 'fsm-parent-work-orders'}
          permanentFilters={
            lineItemsView
              ? ([
                  [
                    ['thirdPartyId', '<>', null],
                    'or',
                    ['thirdPartySource', '<>', null],
                    'or',
                    ['thirdPartyNumber', '<>', null],
                  ],
                  'and',
                  ['isParent', '<>', true],
                  'and',
                  ['parentId', 'isNotNull'],
                ] as unknown as Filter[])
              : ([
                  [
                    ['thirdPartyId', '<>', null],
                    'or',
                    ['thirdPartySource', '<>', null],
                    'or',
                    ['thirdPartyNumber', '<>', null],
                  ],
                  'and',
                  ['isParent', '=', true],
                ] as unknown as Filter[])
          }
          toolbarPrefix={
            <SwitchWrapper>
              <Switch value={lineItemsView} onValueChanged={(e) => setLineItemsView(e.value)} />
              <SwitchLabel>Line Items View</SwitchLabel>
            </SwitchWrapper>
          }
        />
      </If>
      <If condition={tab === 'templates'}>
        <WOTemplate />
      </If>
    </SubPage>
  );
};

export default React.memo(WorkOrdersPage);
